import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiService, User as ApiUser, LoginCredentials, ClientRef } from '@/lib/api';

// Re-exportar a interface User para compatibilidade
export type User = ApiUser;

export interface PendingSelection {
  clients: ClientRef[];
  preSelectToken: string;
}

interface AuthContextType {
  user: ApiUser | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<ApiUser>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  firstAccessUser: ApiUser | null;
  setFirstAccessUser: (user: ApiUser | null) => void;
  availableClients: ClientRef[];
  defaultClientId: number | null;
  selectClientAtLogin: (clientId: number, preSelectToken: string, setAsDefault: boolean) => Promise<ApiUser>;
  switchClient: (clientId: number, setAsDefault?: boolean) => Promise<void>;
  clearDefaultClient: () => Promise<void>;
  pendingSelection: PendingSelection | null;
  beginClientSelection: (clients: ClientRef[], preSelectToken: string) => void;
  cancelClientSelection: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<ApiUser | null>(() => {
    // Tentar recuperar o usuário do localStorage na inicialização
    const savedUser = localStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [firstAccessUser, setFirstAccessUser] = useState<ApiUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Seleção de conta no login multi-conta (em memória; vive só durante a escolha).
  const [pendingSelection, setPendingSelection] = useState<PendingSelection | null>(null);

  const beginClientSelection = (clients: ClientRef[], preSelectToken: string) => {
    setPendingSelection({ clients, preSelectToken });
  };

  const cancelClientSelection = () => setPendingSelection(null);

  useEffect(() => {
    // Verificar se há dados válidos no localStorage primeiro
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        console.log('🔄 [AuthContext] Usuário encontrado no localStorage:', userData);
        setUser(userData);
        setIsLoading(false);
        // Verificar se o token ainda é válido em background
        checkAuthStatus();
        return;
      } catch (error) {
        console.error('❌ [AuthContext] Erro ao parsear usuário do localStorage:', error);
        localStorage.removeItem('user');
      }
    }

    // Se não há dados salvos, verificar autenticação
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    console.log('🔍 [AuthContext] Verificando status de autenticação...');
    console.log('🍪 [AuthContext] Cookies disponíveis:', document.cookie);
    try {
      // Verificar se o usuário está autenticado via cookie
      console.log('🔍 [AuthContext] Chamando apiService.me()...');
      const { user } = await apiService.me();
      console.log('✅ [AuthContext] Usuário encontrado:', user);
      setUser(user);
      localStorage.setItem('user', JSON.stringify(user));
      // Limpar dados antigos se necessário
      localStorage.removeItem('disparos_lotes');
      localStorage.removeItem('dashboard_stats');
    } catch (error) {
      console.error('❌ [AuthContext] Erro ao verificar status de autenticação:', error);
      console.error('❌ [AuthContext] Detalhes do erro:', error);
      setUser(null);
      localStorage.removeItem('user');
      localStorage.removeItem('disparos_lotes');
      localStorage.removeItem('dashboard_stats');
    } finally {
      console.log('🔍 [AuthContext] Finalizando verificação de autenticação');
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    console.log('🔍 [AuthContext] Função login chamada', { email, hasPassword: !!password });
    setIsLoading(true);

    try {
      const credentials: LoginCredentials = { email, password };
      console.log('🔍 [AuthContext] Chamando apiService.login');
      const result = await apiService.login(credentials);

      // Caso 1: 2+ contas sem padrão → exigir seleção (a tela de login trata).
      if ('needsClientSelection' in result && result.needsClientSelection) {
        console.log('🔍 [AuthContext] Seleção de conta necessária', { contas: result.clients.length });
        throw {
          needsClientSelection: true,
          clients: result.clients,
          preSelectToken: result.preSelectToken,
        };
      }

      // Caso 2: primeiro acesso (senha temporária).
      if ('firstAccess' in result && result.firstAccess && result.user) {
        console.log('🔍 [AuthContext] Primeiro acesso detectado');
        if (!result.user.name || !result.user.email || !result.user.id) {
          console.error('❌ [AuthContext] Dados do usuário incompletos no primeiro acesso:', result.user);
        }
        setFirstAccessUser(result.user);
        throw { firstAccess: true, user: result.user, message: 'Primeiro acesso detectado' };
      }

      // Caso 3: login direto (1 conta, padrão válido, ou role não-cliente).
      const authResult = result as { user: ApiUser };
      console.log('✅ [AuthContext] Login bem-sucedido', { user: authResult.user.name, role: authResult.user.role });
      setUser(authResult.user);
      localStorage.setItem('user', JSON.stringify(authResult.user));
      return authResult.user;
    } catch (error) {
      console.error('❌ [AuthContext] Erro no login:', error);
      throw error;
    } finally {
      console.log('🔍 [AuthContext] Finalizando função login');
      setIsLoading(false);
    }
  };

  const register = async (name: string, email: string, password: string) => {
    setIsLoading(true);

    try {
      // Criar usuário como cliente por padrão
      await apiService.register({
        name,
        email,
        password,
        role: 'cliente'
      });
    } catch (error) {
      console.error('Erro no registro:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await apiService.logout();
    } catch (error) {
      console.error('Erro no logout:', error);
    } finally {
      setUser(null);
      localStorage.removeItem('user');
    }
  };

  // Finaliza o login após o usuário escolher a conta (usa o preSelectToken).
  const selectClientAtLogin = async (clientId: number, preSelectToken: string, setAsDefault: boolean) => {
    const result = await apiService.setActiveClient(clientId, { preSelectToken, setAsDefault });
    setUser(result.user);
    localStorage.setItem('user', JSON.stringify(result.user));
    setPendingSelection(null);
    return result.user;
  };

  // Troca a conta ativa já logado. Recarrega a página para reidratar todo o contexto de dados.
  const switchClient = async (clientId: number, setAsDefault?: boolean) => {
    const result = await apiService.setActiveClient(clientId, { setAsDefault });
    localStorage.setItem('user', JSON.stringify(result.user));
    localStorage.removeItem('disparos_lotes');
    localStorage.removeItem('dashboard_stats');
    window.location.reload();
  };

  // Limpa o cliente padrão (volta a perguntar nos próximos logins). Sem reload.
  const clearDefaultClient = async () => {
    if (!user?.client_id) return;
    const result = await apiService.setActiveClient(user.client_id, { clearDefault: true });
    setUser(result.user);
    localStorage.setItem('user', JSON.stringify(result.user));
  };

  const value = {
    user,
    isLoading,
    login,
    register,
    logout,
    isAuthenticated: !!user,
    firstAccessUser,
    setFirstAccessUser,
    availableClients: user?.clients ?? [],
    defaultClientId: user?.default_client_id ?? null,
    selectClientAtLogin,
    switchClient,
    clearDefaultClient,
    pendingSelection,
    beginClientSelection,
    cancelClientSelection,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
